import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, KeyRound, CheckCircle, XCircle } from "lucide-react";
import { portalFetch } from "@/lib/portalApi";
import SiteLink from "@/components/SiteLink";

export const Route = createFileRoute("/portal_/reset-password")({
  component: ResetPasswordPage,
});

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (p: string) => p.length >= 8 },
  { label: "Uppercase letter", test: (p: string) => /[A-Z]/.test(p) },
  { label: "Lowercase letter", test: (p: string) => /[a-z]/.test(p) },
  { label: "A number", test: (p: string) => /\d/.test(p) },
  {
    label: "Special character",
    test: (p: string) => /[!@#$%^&*(),.?":{}|<>_\-+=\\[\]/~`';]/.test(p),
  },
];

function ResetPasswordPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allRulesPassed = PASSWORD_RULES.every((r) => r.test(password));
  const passwordsMatch = password === confirmPassword && password.length > 0;
  const canSubmit = allRulesPassed && passwordsMatch && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !token) return;

    setLoading(true);
    setError(null);

    try {
      await portalFetch("/api/v1/auth/reset-password", {
        method: "POST",
        body: { token, new_password: password },
      });
      setSuccess(true);
    } catch (err: unknown) {
      const detail = (err as { details?: { detail?: string } })?.details
        ?.detail;
      setError(detail || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <PageLayout>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="mx-auto h-12 w-12 text-destructive" />
            <CardTitle className="mt-4">Invalid Reset Link</CardTitle>
            <CardDescription>
              No reset token found. Please request a new password reset.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <SiteLink href="/portal/forgot-password">
              <Button variant="outline">Request Password Reset</Button>
            </SiteLink>
          </CardContent>
        </Card>
      </PageLayout>
    );
  }

  if (success) {
    return (
      <PageLayout>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
            <CardTitle className="mt-4">Password Reset</CardTitle>
            <CardDescription>
              Your password has been successfully changed. You can now sign in
              with your new password.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <SiteLink href="/portal/sign-in">
              <Button>Go to Sign In</Button>
            </SiteLink>
          </CardContent>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <KeyRound className="mx-auto h-12 w-12 text-brand-blue-medium" />
          <CardTitle className="mt-4">Set a new password</CardTitle>
          <CardDescription>
            Enter your new password below. Make sure it meets all the
            requirements.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                autoComplete="new-password"
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                autoComplete="new-password"
                disabled={loading}
                required
              />
              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-destructive">
                  Passwords do not match
                </p>
              )}
            </div>

            {password && (
              <div className="space-y-1.5 rounded-lg bg-slate-50 p-3">
                {PASSWORD_RULES.map((rule) => {
                  const passed = rule.test(password);
                  return (
                    <div
                      key={rule.label}
                      className={`flex items-center gap-2 text-xs ${passed ? "text-green-600" : "text-muted-foreground"}`}
                    >
                      {passed ? (
                        <CheckCircle className="h-3 w-3" />
                      ) : (
                        <span className="h-3 w-3 rounded-full border border-current" />
                      )}
                      {rule.label}
                    </div>
                  );
                })}
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button type="submit" className="w-full" disabled={!canSubmit}>
              {loading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Reset Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </PageLayout>
  );
}

function PageLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-10 sm:px-6">
        <SiteLink
          href="/"
          className="inline-flex items-center gap-4 rounded-3xl border border-slate-200 bg-white px-4 py-2 shadow-sm transition hover:border-slate-300"
          style={{ width: "fit-content" }}
        >
          <img
            src="/Larasoft Logo-landscape.svg"
            alt="Larasoft"
            width={180}
            height={64}
            className="h-12"
            style={{ width: "auto" }}
          />
          <span className="rounded-full bg-brand-blue-light/20 px-3 py-1 text-xs font-bold uppercase tracking-[0.3em] text-brand-blue-dark">
            Client portal
          </span>
        </SiteLink>

        <div className="mt-12 flex justify-center">{children}</div>
      </div>
    </div>
  );
}
